import React, { FC } from 'react'
import styled from 'styled-components'

import { RewardsQuery } from '../../../../../graphql/search'
import { usePractitionerSettings } from '../../../../../lib/practitioner-settings/hooks/usePractitionerSettings'
import { dropZeroCents, formatAsCurrencyWithCommasOrZero } from '../../../../../utils/priceTools'
import { InformationIcon, NoticeCard, NoticeLink, NoticeText } from '../RewardsNotice'
import { RewardsVipDiscountCard } from '../RewardsVipDiscountCard'
import {
  DetailsPayoutsCard,
  DetailsPayoutsCardActions,
  DetailsPayoutsCardAmount,
  DetailsPayoutsCardAmountLabel,
  DetailsPayoutsCardAmountSubtext,
  DetailsPayoutsCardContent,
  DetailsPayoutsCardHeading,
  DetailsPayoutsCardPrimaryButtonLink,
} from './DetailsPayoutsCard'

import BasketSvg from '../../../../../img/rewards/basket.inline.svg'

export interface TotalRewardsCardProps {
  availableBalance: {
    storeCredit: number
    totalBalance: number
  }
  previousSnapshot?: NonNullable<
    NonNullable<RewardsQuery['customer']>['rewardsSnapshots']
  >['nodes'][number]
}

const VipDiscountCard = styled(RewardsVipDiscountCard)`
  padding: 15px 32px;
  width: 100%;
  @media (min-width: 1280px) {
    width: 300px;
  }
`

const NoticeCardContainer = styled.div`
  margin: 25px 0;
`

const BasketIcon = styled(BasketSvg)`
  margin-right: 8px;
  vertical-align: bottom;
`

export const TotalRewardsCard: FC<TotalRewardsCardProps> = ({
  availableBalance: { storeCredit, totalBalance },
  previousSnapshot,
}) => {
  const [settings] = usePractitionerSettings()
  const optedOutOfCommissions = settings?.practitioner?.commissionsOptOutFormReceived ?? false
  const optedOutOfRebates = !settings?.practitioner?.enrolledInRebate

  return (
    <DetailsPayoutsCard>
      <DetailsPayoutsCardContent>
        <DetailsPayoutsCardHeading>Total Rewards</DetailsPayoutsCardHeading>
        {optedOutOfCommissions && optedOutOfRebates ? (
          <NoticeCardContainer>
            <NoticeCard>
              <InformationIcon />
              <NoticeText>
                {`You're missing out on commissions and rebates! `}
                <NoticeLink to="/account/settings/rewards-program">
                  Sign up now to start gaining these benefits.
                </NoticeLink>
              </NoticeText>
            </NoticeCard>
          </NoticeCardContainer>
        ) : (
          <>
            <DetailsPayoutsCardAmountLabel>
              <DetailsPayoutsCardAmount>
                {dropZeroCents(formatAsCurrencyWithCommasOrZero({ amount: totalBalance }))}
              </DetailsPayoutsCardAmount>
              available
            </DetailsPayoutsCardAmountLabel>
            <DetailsPayoutsCardAmountSubtext>
              Includes {dropZeroCents(formatAsCurrencyWithCommasOrZero({ amount: storeCredit }))} in
              store credit.
            </DetailsPayoutsCardAmountSubtext>
          </>
        )}
      </DetailsPayoutsCardContent>
      <DetailsPayoutsCardActions>
        <VipDiscountCard previousSnapshot={previousSnapshot} />
        <DetailsPayoutsCardPrimaryButtonLink to="/products">
          <BasketIcon />
          Shop Now
        </DetailsPayoutsCardPrimaryButtonLink>
      </DetailsPayoutsCardActions>
    </DetailsPayoutsCard>
  )
}
